import {
  buildErrorReportFields,
  clampErrorReportText,
} from "@shared/utils/errorReporting";

type ClientErrorContext = Record<string, string | number | boolean | null | undefined>;

type ReportHandledWebClientErrorArgs = {
  type?: string;
  status?: string;
  safeMessage: string;
  error: unknown;
  context?: ClientErrorContext;
};

const DEDUPE_WINDOW_MS = 10_000;
const recentClientErrorReports = new Map<string, number>();

function cleanupExpiredReports(now: number) {
  for (const [signature, expiresAt] of recentClientErrorReports.entries()) {
    if (expiresAt <= now) {
      recentClientErrorReports.delete(signature);
    }
  }
}

function canUseBrowserReporting(): boolean {
  return typeof window !== "undefined" && typeof fetch === "function";
}

function normalizeContextValue(value: unknown): string | number | boolean | null | undefined {
  if (value === null || value === undefined) {
    return value;
  }

  if (typeof value === "string" || typeof value === "number" || typeof value === "boolean") {
    return value;
  }

  if (typeof value === "bigint") {
    return `${value}n`;
  }

  if (typeof value === "function") {
    return `[function ${value.name || "anonymous"}]`;
  }

  if (value instanceof Error) {
    return value.message || value.name;
  }

  if (typeof Element !== "undefined" && value instanceof Element) {
    const id = value.id ? `#${value.id}` : "";
    const classNames =
      typeof value.className === "string" && value.className.trim().length > 0
        ? `.${value.className.trim().split(/\s+/).join(".")}`
        : "";
    return `[dom ${value.tagName.toLowerCase()}${id}${classNames}]`;
  }

  return `[object ${(value as { constructor?: { name?: string } }).constructor?.name || "Object"}]`;
}

function normalizeContext(
  context: Record<string, unknown>,
): ClientErrorContext {
  const normalized: ClientErrorContext = {};

  for (const [key, value] of Object.entries(context)) {
    normalized[key] = normalizeContextValue(value);
  }

  return normalized;
}

function safeJsonStringify(value: unknown): string {
  const seen = new WeakSet<object>();

  return JSON.stringify(value, (_key, candidate) => {
    if (typeof candidate === "bigint") {
      return `${candidate}n`;
    }

    if (typeof candidate === "function") {
      return `[function ${candidate.name || "anonymous"}]`;
    }

    if (!candidate || typeof candidate !== "object") {
      return candidate;
    }

    if (seen.has(candidate)) {
      return "[circular]";
    }

    seen.add(candidate);
    return candidate;
  });
}

function buildSignature(args: {
  type: string;
  path: string;
  safeMessage: string;
  rawMessage: string;
}) {
  return [args.type, args.path, args.safeMessage, args.rawMessage].join("|");
}

export function reportHandledWebClientError({
  type = "web_client_error",
  status = "error",
  safeMessage,
  error,
  context = {},
}: ReportHandledWebClientErrorArgs): void {
  if (!canUseBrowserReporting()) {
    return;
  }

  const fields = buildErrorReportFields(error, safeMessage);
  const path = `${window.location.pathname}${window.location.search}`;
  const now = Date.now();
  cleanupExpiredReports(now);

  const signature = buildSignature({
    type,
    path,
    safeMessage,
    rawMessage: fields.message,
  });

  const existing = recentClientErrorReports.get(signature);
  if (typeof existing === "number" && existing > now) {
    return;
  }

  recentClientErrorReports.set(signature, now + DEDUPE_WINDOW_MS);

  const normalizedContext = normalizeContext(context as Record<string, unknown>);
  const payload = {
    type,
    status,
    safeMessage,
    path,
    userAgent: typeof navigator !== "undefined" ? navigator.userAgent : undefined,
    context: normalizedContext,
    error: {
      name: fields.name,
      message: fields.message,
      stack: fields.stack ? clampErrorReportText(fields.stack, 8_000) : undefined,
      details: clampErrorReportText(fields.details, 8_000),
      fingerprint: fields.fingerprint,
    },
    timestamp: now,
  };

  void fetch("/api/client-errors", {
    method: "POST",
    headers: {
      "content-type": "application/json",
    },
    keepalive: true,
    body: safeJsonStringify(payload),
  }).catch(() => {
    // Keep client-side reporting best-effort and silent.
  });
}
